import AuthValidationErrors from '@/components/AuthValidationErrors'
import { Hero } from '@/components/Hero'
import { SocialLoginButtons } from '@/components/SocialLoginButtons'
import { ProvinceAndCountrySelect } from '@/components/ProvinceAndCountrySelect'
import { useAuth } from '@/hooks/auth'
import {
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  FormLabel,
  Heading,
  Input,
  Link,
  Stack,
  useToast,
} from '@chakra-ui/react'
import * as React from 'react'
import { useState } from 'react'
import { useRouter } from 'next/router'
import { getQueryString } from '@/lib/getQueryString'
import { NextParsedUrlQuery } from 'next/dist/server/request-meta'
import { captureEvent } from '@sentry/nextjs'

const getRedirectTo = (params: { query: NextParsedUrlQuery }): string => {
  if (typeof params.query.redirectTo === 'string') {
    return params.query.redirectTo
  }
  return '/dashboard'
}

const Register = (): JSX.Element => {
  const router = useRouter()
  const query = getQueryString(router.query)
  const toast = useToast()

  const redirectTo = getRedirectTo({ query: router.query })

  const { register } = useAuth({
    middleware: 'guest',
    redirectIfAuthenticated: redirectTo,
  })

  const [country, setCountry] = useState<string | undefined>('')
  const [email, setEmail] = useState('')
  const [errors, setErrors] = useState([])
  const [firstName, setFirstName] = useState('')
  const [instagram, setInstagram] = useState('')
  const [lastName, setLastName] = useState('')
  const [password_confirmation, setPasswordConfirmation] = useState('')
  const [password, setPassword] = useState('')
  const [province, setProvince] = useState<string | undefined>()
  let [submitting, setSubmitting] = useState(false)

  const submitForm = async (event: React.FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    toast({
      title: 'Submitting registration request',
      description: 'Just a moment…',
      status: 'success',
      duration: 5000,
      isClosable: true,
    })
    await register({
      firstName,
      lastName,
      email,
      instagram,
      state: province,
      country,
      password,
      password_confirmation,
      setErrors,
      redirectIfAuthenticated: redirectTo,
    })
      .then(() => {
        setSubmitting(false)
      })
      .catch(error => {
        setSubmitting(false)
        setErrors(error.errors)
        toast({
          title: 'Error',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        captureEvent(error, { data: 'Error registering' })
      })
  }

  return (
    <>
      <Hero heading='Create an Account' />
      <Container maxW='container.md'>
        <Stack spacing={20} py={20}>
          <SocialLoginButtons />

          <form onSubmit={submitForm}>
            <Heading textAlign='center' mb={8}>
              Or signup with your email address
            </Heading>
            {/* Validation Errors */}
            <AuthValidationErrors errors={errors} />
            <Stack spacing={6} px={{ lg: 32 }}>
              <Box>
                <FormLabel htmlFor='firstName'>First Name</FormLabel>
                <Input
                  id='firstName'
                  type='text'
                  value={firstName}
                  onChange={event => setFirstName(event.target.value)}
                  required
                  autoFocus
                />
              </Box>
              <Box>
                <FormLabel htmlFor='lastName'>Last Name</FormLabel>
                <Input
                  id='lastName'
                  type='text'
                  value={lastName}
                  onChange={event => setLastName(event.target.value)}
                  required
                />
              </Box>
              <Box>
                <FormLabel htmlFor='email'>Email</FormLabel>
                <Input
                  id='email'
                  type='email'
                  value={email}
                  onChange={event => setEmail(event.target.value)}
                  required
                />
              </Box>
              <Box>
                <FormLabel htmlFor='instagram'>
                  Instagram Handle <small>(optional)</small>
                </FormLabel>
                <Input
                  id='instagram'
                  type='instagram'
                  value={instagram}
                  onChange={event => setInstagram(event.target.value)}
                />
              </Box>
              <ProvinceAndCountrySelect
                province={province}
                country={country}
                setProvince={setProvince}
                setCountry={setCountry}
              />
              <Box>
                <FormLabel htmlFor='password'>Password</FormLabel>
                <Input
                  id='password'
                  type='password'
                  value={password}
                  onChange={event => setPassword(event.target.value)}
                  required
                  autoComplete='new-password'
                />
              </Box>
              <div>
                <FormLabel htmlFor='password_confirmation'>
                  Confirm Password
                </FormLabel>

                <Input
                  id='password_confirmation'
                  type='password'
                  value={password_confirmation}
                  onChange={event =>
                    setPasswordConfirmation(event.target.value)
                  }
                  required
                />
              </div>

              <Stack spacing={3}>
                <Checkbox required>
                  <Box as='span' fontSize='15px'>
                    I agree to the{' '}
                    <Link
                      textDecor='underline'
                      href='/terms-of-service'
                      target='_blank'>
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link
                      textDecor='underline'
                      href='/contest-terms-and-conditions'
                      target='_blank'>
                      Contest Terms &amp; Conditions
                    </Link>
                    .
                  </Box>
                </Checkbox>
                <Checkbox required>
                  <Box as='span' fontSize='15px'>
                    I’m more than 12 years old.
                  </Box>
                </Checkbox>
              </Stack>

              <Flex justifyContent='space-between'>
                <Link href={`/login${query}`}>Already registered?</Link>

                <Button
                  size='lg'
                  colorScheme='red'
                  type='submit'
                  isLoading={submitting}>
                  Register
                </Button>
              </Flex>
            </Stack>
          </form>
        </Stack>
      </Container>
    </>
  )
}

export default Register
